import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axiosHttp from "services/axiosHttp"; // Adding the standard HTTP client

export const fetchDistributorSkuSummaryList = createAsyncThunk(
    "DistributorSkuSummarySlice/fetchDistributorSkuSummaryList",
    async ({ page, limit, filters }, { rejectWithValue }) => {
        try {
            const params = new URLSearchParams({
                page: page || 1,
                limit: limit || 20,
                ...(filters?.distributorCode && { distributorCode: filters.distributorCode })
            });
            const response = await axiosHttp.get(`/reports/distributor-sku-summary?${params}`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data || error.message);
        }
    }
);

export const fetchDistributorCurrentStock = createAsyncThunk(
    "DistributorSkuSummarySlice/fetchDistributorCurrentStockDetail",
    async ({ distributorCode }, { rejectWithValue }) => {
        try {
            const response = await axiosHttp.get(`/reports/distributor-sku-summary/${distributorCode}/stock`);
            return response.data;
        } catch (error) {
            return rejectWithValue(error?.response?.data || error.message);
        }
    }
);

const DistributorSkuSummarySlice = createSlice({
    name: "DistributorSkuSummarySlice",
    initialState: {
        listLoading: false,
        listData: [],
        listTotal: 0,
        listPage: 1,

        detailLoading: false,
        detailData: [],

        error: null,
    },
    reducers: {},
    extraReducers: (builder) => {
        // Summary List
        builder.addCase(fetchDistributorSkuSummaryList.pending, (state) => {
            state.listLoading = true;
            state.error = null;
        });
        builder.addCase(fetchDistributorSkuSummaryList.fulfilled, (state, action) => {
            state.listLoading = false;
            try {
                const payload = action.payload || {};
                const arg = action.meta.arg || {};

                // The API sends { status: 'success', data: [...] }
                const rawData = payload.data || payload;
                let extractedData = Array.isArray(rawData) ? rawData : [];

                if (!Array.isArray(rawData)) { // fallback if it was inside something else
                    if (Array.isArray(rawData?.docs)) extractedData = rawData.docs;
                    else if (Array.isArray(rawData?.data)) extractedData = rawData.data;
                    else if (Array.isArray(rawData?.list)) extractedData = rawData.list;
                }

                state.listData = extractedData;

                // Handle total count and page from standard payload.pagination format
                if (payload.pagination) {
                    state.listTotal = payload.pagination.totalRecords || payload.pagination.total || extractedData.length || 0;
                    state.listPage = payload.pagination.currentPage || payload.pagination.page || arg.page || 1;
                } else {
                    // Fallback to older structures or array length
                    state.listTotal = rawData?.totalCount || rawData?.total || rawData?.totalDocs || payload?.total || extractedData.length || 0;
                    if (rawData?.offset !== undefined && rawData?.limit) {
                        state.listPage = Math.floor(rawData.offset / rawData.limit) + 1;
                    } else if (rawData?.page) {
                        state.listPage = rawData.page;
                    } else {
                        state.listPage = arg.page || 1;
                    }
                }
            } catch (err) {
                console.error("Reducer error parsing list data:", err);
                state.listData = [];
            }
        });
        builder.addCase(fetchDistributorSkuSummaryList.rejected, (state, action) => {
            state.listLoading = false;
            state.error = action.payload || action.error?.message;
        });

        // Detail
        builder.addCase(fetchDistributorCurrentStock.pending, (state) => {
            state.detailLoading = true;
            state.error = null;
        });
        builder.addCase(fetchDistributorCurrentStock.fulfilled, (state, action) => {
            state.detailLoading = false;
            try {
                const payload = action.payload || {};

                // The API sends { status: 'success', data: [...] }
                const rawData = payload.data || payload;
                let extractedData = Array.isArray(rawData) ? rawData : [];

                if (!Array.isArray(rawData)) { // fallback if it was inside something else
                    if (Array.isArray(rawData?.docs)) extractedData = rawData.docs;
                    else if (Array.isArray(rawData?.data)) extractedData = rawData.data;
                }

                state.detailData = extractedData;
            } catch (err) {
                console.error("Reducer error parsing detail data:", err);
                state.detailData = [];
            }
        });
        builder.addCase(fetchDistributorCurrentStock.rejected, (state, action) => {
            state.detailLoading = false;
            state.error = action.payload || action.error?.message;
        });
    },
});

export default DistributorSkuSummarySlice.reducer;
